import {
  REMOTE_MUTATION_FINGERPRINT_FIELD_TUPLES,
  type RemoteMutationFingerprintFieldSchema,
  type InstallEpoch,
  type InstallationGeneration,
  type RecoveryEpoch,
  type RemoteMutationActionId,
  type RemoteMutationFingerprintContractVersion,
  type RemoteMutationFingerprintKeyVersion,
  type RemoteMutationIdempotencyFingerprint,
  type RemoteMutationIntentId,
  type RemoteMutationOperation,
  type RemoteMutationOwnershipFingerprint,
  type RemoteMutationPreconditionFingerprint,
  type RemoteMutationProvider,
  type RemoteMutationRequestFingerprint,
  type RemoteMutationResource,
  type RemoteMutationSubject,
  type RemoteMutationTargetFingerprint,
} from './installationIdentityContract';

/**
 * Q9-B1 closes the business-field tuples left intentionally unresolved by
 * Q9-A. This module is representation-only: it imports no Worker runtime,
 * starts no writer, reads no key, and performs no cryptographic operation.
 */

export const REMOTE_MUTATION_IDEMPOTENCY_SOURCE_KINDS = [
  'SEO_RECONCILE_ATTEMPT',
  'INSTALLATION_LIFECYCLE_OPERATION',
  'OPERATOR_REMOTE_MUTATION',
  'DIAGNOSTIC_REMOTE_MUTATION',
] as const;
export type RemoteMutationIdempotencySourceKind =
  (typeof REMOTE_MUTATION_IDEMPOTENCY_SOURCE_KINDS)[number];

export const REMOTE_MUTATION_OWNERSHIP_MARKER_VERSION = 'FMP_Q9_OWNER_V1' as const;
export type RemoteMutationOwnershipMarkerVersion =
  typeof REMOTE_MUTATION_OWNERSHIP_MARKER_VERSION;

export const REMOTE_MUTATION_PAGE_TYPES = [
  'page',
  'raw',
  'contact_form',
  'rss_feed',
  'link',
] as const;
export type RemoteMutationPageType = (typeof REMOTE_MUTATION_PAGE_TYPES)[number];

export const REMOTE_MUTATION_REMOTE_PRESENCE_STATES = ['ABSENT', 'PRESENT'] as const;
export type RemoteMutationRemotePresence =
  (typeof REMOTE_MUTATION_REMOTE_PRESENCE_STATES)[number];

export const REMOTE_MUTATION_FINGERPRINT_DOMAINS = [
  'TARGET',
  'IDEMPOTENCY',
  'REQUEST',
  'OWNERSHIP',
  'PRECONDITION',
] as const;
export type RemoteMutationFingerprintDomain =
  (typeof REMOTE_MUTATION_FINGERPRINT_DOMAINS)[number];
export type RemoteMutationFingerprintFieldName =
  (typeof REMOTE_MUTATION_FINGERPRINT_FIELD_TUPLES)[keyof typeof REMOTE_MUTATION_FINGERPRINT_FIELD_TUPLES][number];

function freezeFingerprintFieldSchema<
  const Schema extends readonly RemoteMutationFingerprintFieldSchema[],
>(schema: Schema): Schema {
  for (const field of schema) {
    Object.freeze(field.allowedTypeTags);
    Object.freeze(field);
  }
  return Object.freeze(schema);
}

export const REMOTE_MUTATION_IDEMPOTENCY_FINGERPRINT_SCHEMA =
  freezeFingerprintFieldSchema([
  { fieldName: 'store_hash', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'recovery_epoch', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'installation_generation', allowedTypeTags: ['U53'] },
  { fieldName: 'install_epoch', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'source_kind', allowedTypeTags: ['ENUM_ASCII'] },
  { fieldName: 'source_id', allowedTypeTags: ['ASCII_ID'] },
] as const satisfies readonly RemoteMutationFingerprintFieldSchema[]);

export const REMOTE_MUTATION_REQUEST_FINGERPRINT_SCHEMA =
  freezeFingerprintFieldSchema([
  { fieldName: 'target_fingerprint', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'operation', allowedTypeTags: ['ENUM_ASCII'] },
  { fieldName: 'expected_remote_id', allowedTypeTags: ['U53', 'NULL'] },
  { fieldName: 'name', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'page_type', allowedTypeTags: ['ENUM_ASCII', 'NULL'] },
  { fieldName: 'url', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'body', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'is_visible', allowedTypeTags: ['ENUM_ASCII', 'NULL'] },
  { fieldName: 'meta_title', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'meta_description', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'search_keywords', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'channel_id', allowedTypeTags: ['U53', 'NULL'] },
  { fieldName: 'parent_id', allowedTypeTags: ['U53', 'NULL'] },
] as const satisfies readonly RemoteMutationFingerprintFieldSchema[]);

export const REMOTE_MUTATION_OWNERSHIP_FINGERPRINT_SCHEMA =
  freezeFingerprintFieldSchema([
  { fieldName: 'ownership_marker_version', allowedTypeTags: ['ENUM_ASCII'] },
  { fieldName: 'store_hash', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'recovery_epoch', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'installation_generation', allowedTypeTags: ['U53'] },
  { fieldName: 'install_epoch', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'target_fingerprint', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'owner_intent_id', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'owner_action_id', allowedTypeTags: ['ASCII_ID'] },
] as const satisfies readonly RemoteMutationFingerprintFieldSchema[]);

export const REMOTE_MUTATION_PRECONDITION_FINGERPRINT_SCHEMA =
  freezeFingerprintFieldSchema([
  { fieldName: 'target_fingerprint', allowedTypeTags: ['ASCII_ID'] },
  { fieldName: 'operation', allowedTypeTags: ['ENUM_ASCII'] },
  { fieldName: 'remote_presence', allowedTypeTags: ['ENUM_ASCII'] },
  { fieldName: 'remote_id', allowedTypeTags: ['U53', 'NULL'] },
  { fieldName: 'ownership_fingerprint', allowedTypeTags: ['ASCII_ID', 'NULL'] },
  { fieldName: 'name', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'page_type', allowedTypeTags: ['ENUM_ASCII', 'NULL'] },
  { fieldName: 'url', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'body', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'is_visible', allowedTypeTags: ['ENUM_ASCII', 'NULL'] },
  { fieldName: 'meta_title', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'meta_description', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'search_keywords', allowedTypeTags: ['NFC_UTF8', 'NULL'] },
  { fieldName: 'channel_id', allowedTypeTags: ['U53', 'NULL'] },
  { fieldName: 'parent_id', allowedTypeTags: ['U53', 'NULL'] },
] as const satisfies readonly RemoteMutationFingerprintFieldSchema[]);

/** One tuple source: Q9-B1 enriches it with the per-field schemas above. */
export const REMOTE_MUTATION_Q9_B1_CLOSED_FIELD_TUPLES =
  REMOTE_MUTATION_FINGERPRINT_FIELD_TUPLES;

/**
 * MAC-derivation semantics only. This deliberately does not discharge
 * `REMOTE_MUTATION_COMPENSATION_OWNERSHIP_BINDING`: a real BigCommerce
 * create/read/compensate marker round-trip remains a later first-writer gate.
 *
 * The eventual remote marker belongs to the exact forward action that created
 * the resource. A compensation reuses those owner coordinates; it must not
 * derive a new ownership MAC from the compensation action.
 */
export const REMOTE_MUTATION_OWNERSHIP_DERIVATION_RULES = {
  scope: 'MAC_DERIVATION_ONLY_NOT_RUNTIME_BINDING',
  markerVersion: REMOTE_MUTATION_OWNERSHIP_MARKER_VERSION,
  ownerCoordinates: ['owner_intent_id', 'owner_action_id'],
  compensationDerivation:
    'REUSE_AND_VERIFY_EXACT_COMPENSATED_FORWARD_OWNER_COORDINATES',
  providerMarkerRoundTrip: 'BLOCKED_Q9_B2_BEFORE_WRITER',
} as const;

export interface RemoteMutationTargetFingerprintInput {
  readonly provider: RemoteMutationProvider;
  readonly resource: RemoteMutationResource;
  readonly subject: RemoteMutationSubject;
  readonly subjectId: string;
  readonly channelId: number;
}

export interface RemoteMutationIdempotencyFingerprintInput {
  readonly storeHash: string;
  readonly recoveryEpoch: RecoveryEpoch;
  readonly installationGeneration: InstallationGeneration;
  readonly installEpoch: InstallEpoch;
  readonly sourceKind: RemoteMutationIdempotencySourceKind;
  readonly sourceId: string;
}

export interface RemoteMutationRequestFingerprintInput {
  readonly targetFingerprint: RemoteMutationTargetFingerprint;
  readonly operation: RemoteMutationOperation;
  readonly expectedRemoteId: number | null;
  readonly name: string | null;
  readonly pageType: RemoteMutationPageType | null;
  readonly url: string | null;
  readonly body: string | null;
  readonly isVisible: boolean | null;
  readonly metaTitle: string | null;
  readonly metaDescription: string | null;
  readonly searchKeywords: string | null;
  readonly channelId: number | null;
  readonly parentId: number | null;
}

export interface RemoteMutationOwnershipFingerprintInput {
  readonly ownershipMarkerVersion: RemoteMutationOwnershipMarkerVersion;
  readonly storeHash: string;
  readonly recoveryEpoch: RecoveryEpoch;
  readonly installationGeneration: InstallationGeneration;
  readonly installEpoch: InstallEpoch;
  readonly targetFingerprint: RemoteMutationTargetFingerprint;
  /** Exact forward creator coordinates, including during compensation. */
  readonly ownerIntentId: RemoteMutationIntentId;
  readonly ownerActionId: RemoteMutationActionId;
}

interface RemoteMutationPreconditionCommon {
  readonly targetFingerprint: RemoteMutationTargetFingerprint;
  readonly operation: RemoteMutationOperation;
}

export interface RemoteMutationAbsentPreconditionFingerprintInput
  extends RemoteMutationPreconditionCommon {
  readonly remotePresence: 'ABSENT';
  readonly remoteId: null;
  readonly ownershipFingerprint: null;
  readonly name: null;
  readonly pageType: null;
  readonly url: null;
  readonly body: null;
  readonly isVisible: null;
  readonly metaTitle: null;
  readonly metaDescription: null;
  readonly searchKeywords: null;
  readonly channelId: null;
  readonly parentId: null;
}

export interface RemoteMutationPresentPreconditionFingerprintInput
  extends RemoteMutationPreconditionCommon {
  readonly remotePresence: 'PRESENT';
  readonly remoteId: number;
  readonly ownershipFingerprint: RemoteMutationOwnershipFingerprint | null;
  readonly name: string;
  readonly pageType: RemoteMutationPageType;
  readonly url: string;
  readonly body: string | null;
  readonly isVisible: boolean;
  readonly metaTitle: string | null;
  readonly metaDescription: string | null;
  readonly searchKeywords: string | null;
  readonly channelId: number;
  readonly parentId: number | null;
}

export type RemoteMutationPreconditionFingerprintInput =
  | RemoteMutationAbsentPreconditionFingerprintInput
  | RemoteMutationPresentPreconditionFingerprintInput;

export interface VersionedRemoteMutationFingerprint<Fingerprint extends string> {
  readonly contractVersion: RemoteMutationFingerprintContractVersion;
  readonly keyVersion: RemoteMutationFingerprintKeyVersion;
  readonly fingerprint: Fingerprint;
}

export type VersionedRemoteMutationTargetFingerprint =
  VersionedRemoteMutationFingerprint<RemoteMutationTargetFingerprint>;
export type VersionedRemoteMutationIdempotencyFingerprint =
  VersionedRemoteMutationFingerprint<RemoteMutationIdempotencyFingerprint>;
export type VersionedRemoteMutationRequestFingerprint =
  VersionedRemoteMutationFingerprint<RemoteMutationRequestFingerprint>;
export type VersionedRemoteMutationOwnershipFingerprint =
  VersionedRemoteMutationFingerprint<RemoteMutationOwnershipFingerprint>;
export type VersionedRemoteMutationPreconditionFingerprint =
  VersionedRemoteMutationFingerprint<RemoteMutationPreconditionFingerprint>;

export const REMOTE_MUTATION_FINGERPRINT_FAILURE_CODES = [
  'INVALID_KEYRING',
  'INVALID_INPUT',
  'UNKNOWN_KEY_VERSION',
  'CRYPTO_FAILURE',
] as const;
export type RemoteMutationFingerprintFailureCode =
  (typeof REMOTE_MUTATION_FINGERPRINT_FAILURE_CODES)[number];

export interface RemoteMutationFingerprintFailure {
  readonly code: RemoteMutationFingerprintFailureCode;
  readonly domain?: RemoteMutationFingerprintDomain;
  /** Closed contract field name only; never the rejected value. */
  readonly field?: RemoteMutationFingerprintFieldName;
}

export type RemoteMutationFingerprintResult<Value> =
  | { readonly ok: true; readonly value: Value }
  | { readonly ok: false; readonly error: RemoteMutationFingerprintFailure };
